import React, { useState, useRef, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Card, CardContent } from '../ui/Card'
import { useAuthStore } from '../../stores/authStore'
import { Shield, ArrowRight, Loader2, ArrowLeft, CheckCircle } from 'lucide-react'

export const OTPVerification: React.FC = () => {
  const { 
    user, 
    currentEmail,
    isSubmitting, 
    error, 
    verifyOtp,
    setCurrentStep 
  } = useAuthStore()

  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isResending, setIsResending] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1) // Only take the last character

    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    
    if (pastedData.length > 0) {
      const newOtp = [...otp]
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || ''
      }
      setOtp(newOtp)
      
      // Focus the last filled input or the first empty one
      const lastFilledIndex = Math.min(pastedData.length - 1, 5)
      inputRefs.current[lastFilledIndex]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const otpString = otp.join('')
    if (otpString.length !== 6) {
      return
    }

    await verifyOtp(otpString)
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    // Simulate resend delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsResending(false)
    setOtp(['', '', '', '', '', ''])
    inputRefs.current[0]?.focus()
  }

  const handleBack = () => {
    setCurrentStep('userInfo')
  }

  const otpString = otp.join('')
  const isOtpComplete = otpString.length === 6

  return (
    <Card className="shadow-lg border-0 bg-white max-w-md mx-auto">
      <CardContent className="p-8">
        <div className="text-center mb-6">
          <div className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Shield className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h2>
          <p className="text-gray-600 mb-1">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-blue-600 font-medium">
            {currentEmail || user?.email}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <div className="flex justify-center gap-2 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="\d{1}"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
                  disabled={isSubmitting}
                />
              ))}
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">
                Enter the 6-digit verification code sent to your email
              </p>
              {currentEmail && (
                <p className="text-xs text-gray-400">
                  Code sent to: <span className="font-medium text-blue-600">{currentEmail}</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3 flex items-center justify-center gap-2"
              disabled={!isOtpComplete || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Code
                  <CheckCircle className="h-4 w-4" />
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="w-full flex items-center justify-center gap-2"
              disabled={isSubmitting}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Secure Access
            </Button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Didn't receive the code?
          </p>
          <Button
            variant="ghost"
            onClick={handleResendOtp}
            disabled={isResending || isSubmitting}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {isResending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Resending...
              </>
            ) : (
              'Resend Code'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
